
import { useState, useEffect, useCallback } from "react";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { handleError } from "@/utils/error-handling";
import { resetPlayedGamesCount } from "@/utils/localStorageUtils";

export const useAuthListener = () => {
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(true);

  const setupAuthListener = useCallback(() => {
    // Enhanced logging for debugging
    console.log('🔍 Auth Listener Setup', {
      fullLocation: window.location,
      hash: location.hash,
      pathname: location.pathname,
      search: location.search
    });

    const handleHashFragment = async () => {
      if (location.hash && location.hash.length > 1) {
        try {
          console.log('🚨 Detailed Hash Fragment Debug', {
            rawHash: location.hash,
            parsedParams: new URLSearchParams(location.hash.substring(1)).toString()
          });
          
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('🔴 Session Retrieval Error:', error);
            throw error;
          }

          if (!data.session) {
            console.log('🕵️ No Current Session, Attempting Token Exchange');
            const hashParams = new URLSearchParams(location.hash.substring(1));
            
            if (hashParams.has("access_token")) {
              console.log('🔑 Tokens Found in Hash', {
                hasAccessToken: hashParams.has("access_token"),
                hasRefreshToken: hashParams.has("refresh_token")
              });

              await supabase.auth.setSession({
                access_token: hashParams.get("access_token") || "",
                refresh_token: hashParams.get("refresh_token") || "",
              });
            } else {
              console.warn('❓ No Tokens Found in Hash');
            }
          }
          
          window.history.replaceState(null, document.title, location.pathname + location.search);
          
        } catch (err) {
          console.error('❌ Authentication Fragment Handling Error:', err);
          handleError(err, "Failed to complete authentication");
        }
      }
    };
    
    const checkSession = async () => {
      try {
        await handleHashFragment();
        
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        if (data.session) {
          console.log('✅ User Authenticated:', data.session.user.email);
          navigate('/');
        }
        setIsChecking(false);
      } catch (err) {
        console.error("Error checking session:", err);
        handleError(err, "Unable to verify login status");
        setIsChecking(false);
      }
    };
    
    checkSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("🔄 Auth State Changed:", { event, hasSession: !!session });
        
        if (event === 'SIGNED_IN' && session) {
          resetPlayedGamesCount();
          
          navigate('/');
          toast({
            title: "Success!",
            description: "You are now logged in",
          });
        }
      }
    );
    
    return subscription;
  }, [location, navigate, supabase.auth, toast]);

  return {
    isChecking,
    setupAuthListener
  };
};
